import { offlineReplies, safeCountries } from './config'
import { staticProfiles } from '../config/staticProfiles'
import Cookies from 'js-cookie'
import { isLtx, ltxGemsFor, videoVersionLabel } from './videoVersions'

const truncate = (str, maxLength = 5) => {
  if (str.length > maxLength) {
    return `${str.slice(0, maxLength)}...`
  }
  return str
}

function getBeforeComma(prompt) {
  return prompt.split(',')[0]
}

function debounce(func, wait) {
  let timeout
  return function (...args) {
    const context = this
    clearTimeout(timeout)
    timeout = setTimeout(() => func.apply(context, args), wait)
  }
}

const containsRestrictedWords = (str, restrictedWords) => {
  const lowerCaseStr = str?.toLowerCase()
  return restrictedWords?.some((restrictedWord) =>
    lowerCaseStr?.includes(restrictedWord?.toLowerCase()),
  )
}

const timeLeftForNewGems = (lastClaimTime) => {
  if (!lastClaimTime) {
    return null
  }

  const now = new Date()
  const diffMilliseconds = now.getTime() - lastClaimTime.getTime()
  const diffMinutes = diffMilliseconds / (1000 * 60)
  const hoursLeft = Math.floor((12 * 60 - diffMinutes) / 60)
  const minutesLeft = Math.round(60 - (diffMinutes % 60))

  // console.log(
  //   `lastClaimTime: ${lastClaimTime.toISOString()}, \ntimeNow: ${now.toISOString()}, \nhoursLeft: ${hoursLeft}, \nminutesLeft: ${minutesLeft}`
  // )

  if (hoursLeft >= 0 && minutesLeft >= 0) {
    return `${hoursLeft}h ${minutesLeft}m`
  }

  return null
}

function isUrl(s) {
  if (!s || typeof s !== 'string') {
    return false
  }

  return (
    s.startsWith('http://') ||
    s.startsWith('https://') ||
    s.startsWith('/assets/') ||
    s.includes('/assets') ||
    s.includes('data:image/jpeg;base64,')
  )
}

const dataUrlToBlob = async (dataUrl) => {
  const res = await fetch(dataUrl)
  return res.blob()
}

const saveBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()

  setTimeout(() => URL.revokeObjectURL(url), 30_000)
}

// Fallback when fetch+blob isn't possible (e.g. a cross-origin asset whose
// host doesn't allow CORS, which is common on localhost). The `download`
// attribute is ignored cross-origin, so this at least opens the asset in a new
// tab where it can be saved manually.
const forceDownloadViaLink = (url, filename) => {
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.target = '_blank'
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  a.remove()
}

// react-device-detect (with ua-parser-js, about 10 kB gzipped) loads when a download or an auto-save
// starts, not with this module, which every page loads (plan step W2, 2026-10-07). The download
// functions start loading it before they fetch the media, so it is there by the time it is asked.
// If it cannot load, the flags read as false (a plain download, as on a desktop) and the next
// download tries again.
let deviceModule
const loadDevice = () =>
  (deviceModule ??= import('react-device-detect').catch(() => {
    deviceModule = undefined
    return {}
  }))

const tryNativeShare = async (blob, filename, type) => {
  const { isAndroid, isIOS, isIPad13 } = await loadDevice()
  const isMobileOs = isIOS || isAndroid || isIPad13

  if (!isMobileOs || !navigator.share || !navigator.canShare) {
    return false
  }

  try {
    const file = new File([blob], filename, { type })

    if (!navigator.canShare({ files: [file] })) {
      return false
    }

    await navigator.share({
      files: [file],
      title: filename,
    })

    return true
  } catch (err) {
    console.log('Native share not used:', err)
    return false
  }
}

export const saveImage = async (dataUrl, fileName) => {
  loadDevice()
  try {
    const blob = await dataUrlToBlob(dataUrl)
    const type = blob.type || 'image/jpeg'

    const shared = await tryNativeShare(blob, fileName, type)
    if (shared) return

    saveBlob(blob, fileName)
  } catch (err) {
    console.warn('Share/download failed for dataUrl image', err)

    // last-resort fallback
    const aElement = document.createElement('a')
    aElement.href = dataUrl
    aElement.download = fileName
    aElement.target = '_blank'
    document.body.appendChild(aElement)
    aElement.click()
    document.body.removeChild(aElement)
  }
}

export const downloadImage = async (url) => {
  loadDevice()
  try {
    const res = await fetch(url)
    const blob = await res.blob()
    const typedBlob = new Blob([blob], { type: 'image/jpeg' })

    const shared = await tryNativeShare(typedBlob, 'image.jpg', 'image/jpeg')
    if (shared) return

    saveBlob(typedBlob, 'image.jpg')
  } catch (err) {
    console.error('Image download failed, falling back to direct link:', err)
    forceDownloadViaLink(url, 'image.jpg')
  }
}

export const downloadVideo = async (url) => {
  loadDevice()
  try {
    const res = await fetch(url)
    const blob = await res.blob()
    const typedBlob = new Blob([blob], { type: 'video/mp4' })

    const shared = await tryNativeShare(typedBlob, 'video.mp4', 'video/mp4')
    if (shared) return

    saveBlob(typedBlob, 'video.mp4')
  } catch (err) {
    console.error('Video download failed, falling back to direct link:', err)
    forceDownloadViaLink(url, 'video.mp4')
  }
}
// JSZip and file-saver load when a zip is made, not with this module (plan step W2, 2026-10-07):
// AuthContext imports this file, so whatever it imports at the top is in every page's first load,
// and JSZip brings Node's stream, buffer and util polyfills and pako with it (about 50 kB gzipped).
const loadZipTools = async () => {
  const [{ default: JSZip }, { saveAs }] = await Promise.all([
    import('jszip'),
    import('file-saver'),
  ])
  return { JSZip, saveAs }
}

const downloadAllImages = async (imageHistory) => {
  const { JSZip, saveAs } = await loadZipTools()
  const zip = new JSZip()

  for (let i = 0; i < imageHistory.length; i++) {
    const img = imageHistory[i]

    if (isUrl(img)) {
      // If the image is a URL, fetch it
      try {
        const response = await fetch(img)
        const blob = await response.blob()
        zip.file(`image_${i}.jpg`, blob)
      } catch (error) {
        console.error(`Failed to fetch image at URL: ${img}`, error)
      }
    } else {
      // If the image is a base64 data URI, add it directly to the zip
      const dataURI = `data:image/jpeg;base64,${img}`
      const blob = await fetch(dataURI).then((res) => res.blob())
      zip.file(`image_${i}.jpg`, blob)
    }
  }

  const content = await zip.generateAsync({ type: 'blob' })
  saveAs(content, 'images.zip')
}

export const downloadAllVideos = async (videoQueryResults) => {
  const { JSZip, saveAs } = await loadZipTools()
  const zip = new JSZip()
  let added = 0

  for (let i = 0; i < videoQueryResults.length; i++) {
    const item = videoQueryResults[i]
    const url = item?.final_response?.video?.[0] // <-- your nested URL

    if (!url || !isUrl(url)) {
      console.warn(`Skipping index ${i}: missing/invalid video[0]`, item)
      continue
    }

    try {
      const res = await fetch(url)
      if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText}`)

      const blob = await res.blob()
      if (!blob || blob.size === 0)
        throw new Error('Empty blob (possibly blocked)')

      zip.file(`video_${i}.mp4`, blob)
      added++
    } catch (err) {
      console.error(`Failed to fetch video at ${url}`, err)
    }
  }

  if (added === 0) {
    console.warn(
      'No videos added. If errors show CORS/Failed to fetch, you need a proxy or host-side CORS.',
    )
    return
  }

  const content = await zip.generateAsync({ type: 'blob' })
  saveAs(content, 'videos.zip')
}

/**
 * Zip a mixed set of session media into one archive and save it. Each item is
 * `{ type: 'image' | 'video', src }` where `src` is already a fetchable string
 * (a remote URL or a `data:` URI) — the caller resolves CDN hosts / base64 so
 * this stays presentation-agnostic. Items that fail to fetch are skipped (one
 * bad URL never aborts the rest) and returned in `failed` so the caller can fall
 * back per item — e.g. temp-bucket video URLs that lack CORS can't be read into a
 * client-side zip at all. Returns `{ added, failed }`.
 */
export const downloadAllMedia = async (items, zipName = 'download.zip') => {
  const { JSZip, saveAs } = await loadZipTools()
  const zip = new JSZip()
  let added = 0
  const failed = []

  await Promise.all(
    (items || []).map(async (item, i) => {
      if (!item?.blob && !item?.src) return
      try {
        // Prefer bytes we already hold (cached Blobs from Download All) — no fetch,
        // no base64 round-trip. Fall back to fetching a URL / data-URI src.
        let blob
        if (item.blob instanceof Blob) {
          blob = item.blob
        } else {
          const res = await fetch(item.src)
          if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText}`)
          blob = await res.blob()
        }
        if (!blob || blob.size === 0) throw new Error('Empty blob')
        const name = item.type === 'video' ? `video_${i}.mp4` : `image_${i}.jpg`
        zip.file(name, blob)
        added++
      } catch (err) {
        // Cross-origin media without CORS (temp video buckets) lands here; the
        // caller downloads these individually via the native-share / link path.
        console.warn(
          `downloadAllMedia: not zippable, will retry individually`,
          item?.src,
          err?.message,
        )
        failed.push(item)
      }
    }),
  )

  if (added > 0) {
    const content = await zip.generateAsync({ type: 'blob' })
    saveAs(content, zipName)
  }

  return { added, failed }
}

const activeButtonStyle = 'bg-[#FEFFFF] text-[var(--foreground-bg-color)]'
const inactiveButtonStyle =
  'bg-[var(--foreground-bg-color)] text-[#FEFFFF] hover:bg-[var(--secondary-bg-color)]'

function inverseFunction(cfg_scale) {
  const creativity = -10 * cfg_scale + 115
  return creativity
}

const getCreativity = (creativity) => {
  switch (creativity) {
    case 70:
      return 'Creative'
    case 50:
      return 'Balanced'
    default:
      return 'Precise'
  }
}

function getAspectRatio(width, height) {
  if (width === height) {
    return 'Square'
  } else if (width < height) {
    return 'Portrait'
  }
  return 'Wide'
}

function getAspectRatioNumber(aspect) {
  if (aspect === 'Square') {
    return '512x512'
  } else if (aspect === 'Portrait') {
    return '512x768'
  }
  return '768x512'
}

// Video aspect label from raw dimensions. Matches InputsVideo's runtime rule (a 5%
// tolerance around 1:1 counts as Square) so an image and its animation agree.
function aspectFromRatio(width, height) {
  if (!width || !height) return null
  const ratio = width / height
  if (Math.abs(ratio - 1) < 0.05) return 'Square'
  return ratio > 1 ? 'Wide' : 'Portrait'
}

// Resolve a video aspect label from an image URL/base64 when dimensions aren't known
// up front (e.g. a session-feed item). Never rejects — falls back to 'Portrait' so an
// animate can still fire if the probe fails.
function aspectFromImageSrc(src) {
  return new Promise((resolve) => {
    if (!src || typeof window === 'undefined') return resolve('Portrait')
    const img = new window.Image()
    img.onload = () =>
      resolve(aspectFromRatio(img.width, img.height) || 'Portrait')
    img.onerror = () => resolve('Portrait')
    img.src = isUrl(src) ? src : `data:image/jpeg;base64,${src}`
  })
}

// Single source of truth for video gem cost (mirrors the InputsVideo cost bar):
// video5 = by clip length + optional 48fps surcharge, video4 High = 25, video4 = 15, older versions = 20;
// +3 audio, +1 character.
// Flame ("XL 2") models are served only by the image backend (same convention as
// the `model.includes('Flame')` routing check in Create.jsx); for text-to-video
// their base image is generated there at a higher flat cost than the standard XL
// base image (~+2 gems). Surface that so the displayed price matches what's
// charged. Only applies to txt2vid (prompt → video); img2vid supplies its own
// image, so no base image is generated.
// Flame image price per model — mirrors `gem_cost` in the backend's var/models_data.py (4
// unless a model sets its own; none does since Hardcore Flame dropped to 4 on 2026-09-24).
const FLAME_IMAGE_GEMS = {}
export const flameImageGems = (model) => FLAME_IMAGE_GEMS[model] ?? 4

// A Flame model's paid High detail render, chosen as quality Max on the Quality row, in gems on
// top of its price — mirrors `high_detail.gem_cost` in var/models_data.py. 0 = no such option
// (the model's Quality row is hidden and it always renders at Extreme).
const FLAME_HIGH_DETAIL_GEMS = { 'Hardcore Flame': 4 }
export const flameHighDetailGems = (model) => FLAME_HIGH_DETAIL_GEMS[model] ?? 0

// Hardcore Flame renders like Cinematic Flame and takes the same poses and expressions, so the
// config/poses.js `enabled` lists (which name 'Cinematic Flame') apply to it through this.
export const poseListModel = (model) =>
  model === 'Hardcore Flame' ? 'Cinematic Flame' : model

// Its graph has no pose ControlNet: the backend refuses a pose reference (Custom pose) for it.
export const supportsCustomPose = (model) => model !== 'Hardcore Flame'

// The standard XL base image a Flame one replaces costs 2.
const FLAME_VIDEO_SURCHARGE = (model) => flameImageGems(model) - 2

function getVideoGemCost({
  videoQuality,
  videoVersion,
  audioEnabled,
  charImageVideo,
  model,
  hasReferenceImage,
  videoLength,
  videoFps,
  videoResolution,
} = {}) {
  // video5 is priced by clip length, with audio included — it renders sound in the same job, so there is
  // no separate audio pass to bill and no quality tier to pick. The backend normalises those
  // fields away, so adding their surcharges here would just overstate the price.
  const base = isLtx(videoVersion)
    ? ltxGemsFor(videoLength, videoFps, videoResolution)
    : videoVersion === 'video4'
      ? videoQuality === 'High'
        ? 25
        : 15
      : 20
  const flameSurcharge =
    !hasReferenceImage && model?.includes('Flame')
      ? FLAME_VIDEO_SURCHARGE(model)
      : 0
  const audioSurcharge = !isLtx(videoVersion) && audioEnabled ? 3 : 0
  return base + flameSurcharge + audioSurcharge + (charImageVideo ? 1 : 0)
}

function growthFunction(realNumber) {
  return realNumber
}

export {
  truncate,
  debounce,
  containsRestrictedWords,
  timeLeftForNewGems,
  isUrl,
  activeButtonStyle,
  inactiveButtonStyle,
  inverseFunction,
  getCreativity,
  getAspectRatio,
  growthFunction,
  getBeforeComma,
  downloadAllImages,
  getAspectRatioNumber,
  aspectFromRatio,
  aspectFromImageSrc,
  getVideoGemCost,
}

export function getDetails(value) {
  const intValue = parseInt(value, 10)

  switch (intValue) {
    case -2:
    case -1.5:
      return 'Bare'
    case -1:
    case -0.5:
      return 'Light'
    case 0:
      return 'Neutral'
    case 1:
      return 'Sharp'
    case 2:
    case 1.5:
      return 'Intense'
    default:
      return ''
  }
}

// Remembers its answer for the last cookie string it saw (plan step W3, 2026-10-07): Card asks on
// every render of every card, and each Cookies.get splits and decodes every cookie (112 of the 165
// cookie reads while the landing page starts). The answer is still a function of the cookies alone.
let lastCookies
let lastSafeCountry = false
export function isSafeCountry() {
  if (typeof document === 'undefined') return false
  const cookies = document.cookie
  if (cookies !== lastCookies) {
    lastCookies = cookies
    const country = Cookies.get('country')
    lastSafeCountry = !!country && safeCountries.includes(country.toUpperCase())
  }
  return lastSafeCountry
}

export const formatGems = (num) => {
  num = Math.floor(num) // Ensure that the number is an integer
  if (num >= 100000) {
    return `${(num / 1000).toFixed(0)}K` // The toFixed(0) here will ensure the division result is also an integer
  }
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

export const formatNumber = (num) => {
  if (num >= 1e6) return `${(num / 1e6).toFixed(1).replace(/\.0$/, '')}M`
  if (num >= 1e3) return `${(num / 1e3).toFixed(1).replace(/\.0$/, '')}K`
  return num
}

export const getModelName = (
  model,
  video = false,
  videoVersion = 3.1,
  includeModelInBrackets = false,
) => {
  if (video) {
    // Interpolating the raw number printed "Video v5" for the voice model — a version string the
    // product does not use anywhere the user can see. One label helper so the card badge, this
    // hover label and the /image details row can never disagree about what a version is called.
    const versionLabel = `Video ${videoVersionLabel(videoVersion)}`

    if (includeModelInBrackets) {
      const baseName = getModelName(model, false) ?? model
      return `${versionLabel} (${baseName})`
    }

    return versionLabel
  }

  switch (model) {
    case 'Hyperreal':
      return 'Art'
    case 'Anime':
      return 'Anime'
    case 'Hyperanime':
      return 'Hyperanime'
    case 'Real':
      return 'Cinematic'
    case 'Fur':
      return 'Fur'
    case 'Furtoon':
      return 'Furtoon'
    case 'K-Pop':
      return 'K-Pop'
    case 'Anime XL':
      return 'Anime XL'
    case 'Cinematic XL':
      return 'Cinematic XL'
    case 'Hardcore XL':
      return 'Hardcore XL'
    case 'Pony XL':
      return 'Hyperreal XL+ (legacy)'
    case 'Pony Anime XL':
      return 'Anime XL+'
    case 'Pony Photo XL':
      return 'Photo XL+ (legacy)'
    case 'Pony Render XL':
      return 'Render XL+'
    case 'Pony Illustration XL':
      return 'Illustration XL+'
    case 'Pony XL 2':
      return 'Hyperreal XL+'
    case 'Pony Photo XL 2':
      return 'Photo XL+'
    case 'Cinematic Flame':
      return 'Realism XL 2'
    case 'Hardcore Flame':
      return 'Hardcore XL 2'
    case 'Anime Flame':
      return 'Anime XL 2'
    default:
      return null
  }
}

export const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

// Attribute sliders (age/weight/breast/ass/penis) must be persisted as numbers.
// Cloned/remixed payloads can carry them as strings, which the backend stores
// verbatim and then crashes on in numeric feed filters (e.g. get_explore_feed's
// `slider <= -0.25`). Coerce to a number, or null when absent/invalid.
export const toSliderNumber = (value) => {
  if (value === null || value === undefined || value === '') return null
  const num = Number(value)
  return Number.isNaN(num) ? null : num
}

export function capitalizeFirstLetter(string = '') {
  return string.charAt(0).toUpperCase() + string.slice(1)
}

export const randomizeSeed = (setSeed) => {
  const newSeed = Math.floor(Math.random() * 4000000000).toString()
  setSeed(newSeed)
  return newSeed
}

export const obscrureKey = (apiKey) => {
  // Check if apiKey is not null and has a sufficient length to obscure
  if (apiKey && apiKey.length > 6) {
    return `${apiKey.substring(0, 3)}...${apiKey.substring(apiKey.length - 4)}`
  }
  return apiKey // Return the original key if it's too short or null
}

export const base64ToBlob = (base64, contentType = '', sliceSize = 512) => {
  // Ensure base64 is properly padded
  const base64Padded = base64.padEnd(
    base64.length + ((4 - (base64.length % 4)) % 4),
    '=',
  )

  try {
    const byteCharacters = atob(base64Padded) // Decode base64 string
    const byteArrays = []

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize)

      const byteNumbers = new Array(slice.length)
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i)
      }

      const byteArray = new Uint8Array(byteNumbers)
      byteArrays.push(byteArray)
    }

    const blob = new Blob(byteArrays, { type: contentType })
    return URL.createObjectURL(blob)
  } catch (error) {
    console.error('Base64 decoding failed: ', error)
    return null
  }
}

export function capitalizeNames(string) {
  if (!string) {
    return ''
  }
  return string
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

export const getRandomOfflineReply = (name) => {
  const index = Math.floor(Math.random() * offlineReplies.length)
  // Directly replace 'char' with the name, case-insensitive. This won't handle the possessive form explicitly.
  let reply = offlineReplies[index]
  reply = reply.replace(/char/gi, name) // Replace 'char' with 'name', ignoring case
  return reply
}

export const processMessageContent = (content) => {
  const parts = []
  let lastIndex = 0

  const addRemainingChat = (index) => {
    if (index > lastIndex) {
      parts.push({ content: content.slice(lastIndex, index), type: 'chat' })
    }
  }

  for (let i = 0; i < content?.length; i++) {
    if (content[i] === '*') {
      const endIdx = content.indexOf('*', i + 1)
      if (endIdx !== -1) {
        addRemainingChat(i)
        parts.push({ content: content.slice(i, endIdx + 1), type: 'narration' })
        i = endIdx
        lastIndex = endIdx + 1
      }
    } else if (content[i] === '"') {
      const endIdx = content.indexOf('"', i + 1)
      if (endIdx !== -1) {
        addRemainingChat(i)
        // Keep the quotation marks in the content
        parts.push({ content: content.slice(i, endIdx + 1), type: 'dialogue' })
        i = endIdx
        lastIndex = endIdx + 1
      }
    }
  }

  addRemainingChat(content?.length)

  return parts
}

export const safeJSONParse = (value, defaultValue) => {
  try {
    return JSON.parse(value)
  } catch (e) {
    console.error('Error parsing JSON:', e)
    return defaultValue
  }
}

export const selectOfflineSelfie = (id) => {
  // Try to find a static image object with the matching id
  const foundImage = staticProfiles.find((image) => image.id === id)

  // If found and offlineSelfie exists, return the offlineSelfie image
  if (foundImage && foundImage.offlineSelfie) {
    return foundImage.offlineSelfie
  }
  // If not found or offlineSelfie doesn't exist, default to a predefined image or behavior
  // This could be a random selection from all images, a default image, or null
  const defaultImage = 'path/to/default/image.jpg' // Example path, replace with an actual default image path or another logic
  return defaultImage
}

export const stripHTML = (html) => {
  const doc = new DOMParser().parseFromString(html, 'text/html')
  return doc.body.textContent || ''
}

export function getEmotionsString(emotions) {
  switch (emotions) {
    case 0:
      return 'Troubled'
    case 1:
      return 'Balanced'
    case 2:
      return 'Joyful'
    default:
      return 'Balanced'
  }
}

export function getOpennessString(openness) {
  switch (openness) {
    case 0:
      return 'Modest'
    case 1:
      return 'Balanced'
    case 2:
      return 'Adventurous'
    default:
      return 'Balanced'
  }
}

export function mapValueToPercentage(value) {
  const clampedValue = Math.max(-1, Math.min(1, value))
  const result = Math.round(clampedValue * 100)

  return result === 0 ? (
    <>
      <span className="text-xs">Default</span>{' '}
    </>
  ) : result > 0 ? (
    <>
      +{result} <span className="text-xs">%</span>
    </>
  ) : (
    <>
      {result} <span className="text-xs">%</span>
    </>
  )
}

export function isSocialUid(uid) {
  const AUTHORIZED_UIDS = [
    '57qlmsb4JlTEpZmuooqlTgj1rLy2',
    'lq8YecP5Wzbqy3bRG9LIxyLDj9b2',
    'fWdxN62YvQOIjvy9n9rFpAFCYgm2',
  ]

  return AUTHORIZED_UIDS.includes(uid)
}

export function timeAgo(dateString) {
  const now = new Date()
  const created = new Date(dateString)
  const seconds = Math.floor((now - created) / 1000)

  const intervals = [
    { label: 'hour', seconds: 3600 },
    { label: 'minute', seconds: 60 },
    { label: 'second', seconds: 1 },
  ]

  for (const interval of intervals) {
    const count = Math.floor(seconds / interval.seconds)
    if (count >= 1) {
      return `${count} ${interval.label}${count !== 1 ? 's' : ''} ago`
    }
  }

  return 'Just now'
}

export function shuffleArray(arr) {
  // Return a new array in randomized order; original remains unchanged.
  return [...arr].sort(() => Math.random() - 0.5)
}

export const autoDownloadUrls = async (urls) => {
  const { isSafari } = await loadDevice()
  if (isSafari) return
  if (!urls?.length) return

  for (const item of urls) {
    try {
      // If it's not a URL, treat as raw base64 jpeg and download via saveImage
      if (!isUrl(item)) {
        saveImage(`data:image/jpeg;base64,${item}`, 'image.jpg')
        continue
      }

      // If it's already a data URL jpeg, download via saveImage
      if (item.includes('data:image/jpeg;base64,')) {
        saveImage(item, 'image.jpg')
        continue
      }

      // Otherwise it should be a URL (and per your note, only mp4 URLs should be here)
      const url = item

      const res = await fetch(url, { mode: 'cors' })
      const blob = await res.blob()

      const objectUrl = URL.createObjectURL(blob)

      const a = document.createElement('a')
      a.href = objectUrl
      a.download = url.split('/').pop() || 'file.mp4'
      document.body.appendChild(a)
      a.click()
      a.remove()

      URL.revokeObjectURL(objectUrl)
    } catch (e) {
      console.warn('Auto Save download failed for:', item, e)
    }
  }
}

const COOKIE_NAME = 'device_id'

// crypto.randomUUID() only exists in secure contexts (https:// or localhost).
// Over http:// on a LAN IP (e.g. testing on a phone) it's undefined, so fall
// back to getRandomValues (available everywhere) and finally to Math.random.
function generateUUID() {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID()
  }

  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.getRandomValues === 'function'
  ) {
    const bytes = crypto.getRandomValues(new Uint8Array(16))
    bytes[6] = (bytes[6] & 0x0f) | 0x40 // version 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80 // variant 10
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0'))
    return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex
      .slice(6, 8)
      .join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

export function getOrCreateDeviceId() {
  let deviceId = Cookies.get(COOKIE_NAME)

  if (!deviceId) {
    deviceId = generateUUID()

    Cookies.set(COOKIE_NAME, deviceId, {
      expires: 365,
      sameSite: 'Lax',
      secure: window.location.protocol === 'https:',
      path: '/',
    })
  }

  return deviceId
}
